// Professional forecast Excel workbook: Dashboard + Baseline + Rolling + Actual.
// Built on the shared styled-workbook helper.
import crmApi from './crmApi';
import { downloadStyledWorkbook } from './excelExport';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const r3 = n => Math.round((Number(n) || 0) * 1000) / 1000;

function cellMetrics(v, pricing) {
  const u = Number(v.amount) || 0;
  if (v.frozen && v.snap_revenue != null && Number(v.snap_revenue) !== 0)
    return { units: u, cost: r3(u * Number(v.snap_cost || 0)), sales: r3(u * Number(v.snap_price || 0)), rev: Number(v.snap_revenue) };
  const p = pricing[v.category_id] || { price: 0, cost: 0, margin: 0 };
  return { units: u, cost: r3(u * p.cost), sales: r3(u * p.price), rev: r3(u * (p.margin != null ? p.margin : p.price - p.cost)) };
}

// Build the month-by-month rows for one forecast's values (grouped customer→product)
function gridRows(values, pricing, catName, acctName) {
  const lines = {};
  values.forEach(v => {
    if (!v.category_id || !v.account_id) return;
    const key = `${v.account_id}|${v.category_id}`;
    lines[key] = lines[key] || { account_id: v.account_id, category_id: v.category_id, months: {} };
    lines[key].months[v.month] = v;
  });
  const rows = [];
  Object.values(lines).forEach(l => {
    const row = { customer: acctName(l.account_id), product: catName(l.category_id) };
    let yU = 0, yRev = 0;
    MONTHS.forEach((m, i) => {
      const v = l.months[i + 1];
      const x = v ? cellMetrics(v, pricing) : { units: 0, rev: 0 };
      row[`m${i}`] = x.units || '';
      yU += x.units; yRev += x.rev;
    });
    row.year_units = yU; row.year_rev = r3(yRev);
    rows.push(row);
  });
  return rows.sort((a, b) => a.customer.localeCompare(b.customer) || a.product.localeCompare(b.product));
}

function gridSheet(name, title, rows) {
  const columns = [
    { header: 'Customer', key: 'customer', width: 26 },
    { header: 'Product', key: 'product', width: 22 },
    ...MONTHS.map((m, i) => ({ header: m, key: `m${i}`, width: 8, align: 'center' })),
    { header: 'Year Units', key: 'year_units', width: 12, align: 'right' },
    { header: 'Year Revenue', key: 'year_rev', width: 14, align: 'right', numFmt: '#,##0.000' },
  ];
  const totalRow = { customer: 'TOTAL', year_units: rows.reduce((s, r) => s + (Number(r.year_units) || 0), 0), year_rev: r3(rows.reduce((s, r) => s + (Number(r.year_rev) || 0), 0)) };
  return { name, title, subtitle: `Generated ${new Date().toLocaleDateString()}`, columns, rows, totalRow };
}

// Monthly detail: one row per (month, customer, product) with units/cost/sales/revenue.
function monthlyDetailSheet(name, title, values, pricing, catName, acctName) {
  const rows = [];
  for (let m = 1; m <= 12; m++) {
    const monthVals = values.filter(v => v.month === m && v.category_id && v.account_id);
    monthVals
      .map(v => ({ v, x: cellMetrics(v, pricing) }))
      .filter(({ x }) => x.units > 0)
      .sort((a, b) => acctName(a.v.account_id).localeCompare(acctName(b.v.account_id)))
      .forEach(({ v, x }) => rows.push({
        month: MONTHS[m - 1], customer: acctName(v.account_id), product: catName(v.category_id),
        units: x.units, cost: r3(x.cost), sales: r3(x.sales), revenue: r3(x.rev),
      }));
  }
  const columns = [
    { header: 'Month', key: 'month', width: 10 },
    { header: 'Customer', key: 'customer', width: 26 },
    { header: 'Product', key: 'product', width: 22 },
    { header: 'Units', key: 'units', width: 10, align: 'right' },
    { header: 'Cost', key: 'cost', width: 14, align: 'right', numFmt: '#,##0.000' },
    { header: 'Sales', key: 'sales', width: 14, align: 'right', numFmt: '#,##0.000' },
    { header: 'Revenue', key: 'revenue', width: 14, align: 'right', numFmt: '#,##0.000' },
  ];
  const totalRow = {
    month: 'TOTAL',
    units: rows.reduce((s, r) => s + (Number(r.units) || 0), 0),
    cost: r3(rows.reduce((s, r) => s + (Number(r.cost) || 0), 0)),
    sales: r3(rows.reduce((s, r) => s + (Number(r.sales) || 0), 0)),
    revenue: r3(rows.reduce((s, r) => s + (Number(r.revenue) || 0), 0)),
  };
  return { name, title, subtitle: `Generated ${new Date().toLocaleDateString()}`, columns, rows, totalRow };
}

export async function downloadForecastWorkbook(year) {
  // gather data
  const [analytics, cats, accounts, pricing, forecasts, soGrid, products] = await Promise.all([
    crmApi.get(`/forecast-analytics/${year}`).catch(() => null),
    crmApi.get('/categories').catch(() => []),
    crmApi.get('/accounts').catch(() => []),
    crmApi.get('/category-pricing').catch(() => ({})),
    crmApi.get(`/forecasts?year=${year}`).catch(() => []),
    crmApi.get(`/forecast-sales-orders/${year}`).catch(() => null),
    crmApi.get('/products').catch(() => []),
  ]);
  const catName = id => (cats.find(c => c.id === id) || {}).name || 'Product';
  const acctName = id => (accounts.find(a => a.id === id) || {}).name || 'Customer';

  const baseline = forecasts.find(f => f.forecast_type === 'target' && f.is_active)
    || forecasts.find(f => f.forecast_type === 'target' && f.status === 'approved')
    || forecasts.find(f => f.forecast_type === 'target');
  const rolling = forecasts.find(f => f.forecast_type === 'forecast');
  const actual = forecasts.find(f => f.forecast_type === 'actual');

  const loadVals = async (f) => f ? (await crmApi.get(`/forecasts/${f.id}`)).values || [] : [];
  const [bVals, rVals, aVals] = await Promise.all([loadVals(baseline), loadVals(rolling), loadVals(actual)]);

  const sheets = [];

  // 1) Dashboard sheet
  if (analytics) {
    const t = analytics.totals || {}; const v = analytics.variance || {};
    sheets.push({
      name: 'Dashboard',
      title: `Sales Forecast Summary — ${year}`,
      subtitle: `Generated ${new Date().toLocaleDateString()}`,
      columns: [
        { header: 'Metric', key: 'metric', width: 34 },
        { header: 'Value', key: 'value', width: 20, align: 'right', numFmt: '#,##0.000' },
      ],
      rows: [
        { metric: 'Baseline Revenue', value: r3(t.target) },
        { metric: 'Rolling Revenue', value: r3(t.forecast) },
        { metric: 'Actual Revenue', value: r3(t.performance) },
        { metric: 'Forecast Accuracy (%)', value: analytics.accuracy_pct || 0 },
        { metric: 'Variance — Rolling vs Baseline', value: r3(v.forecast_vs_target) },
        { metric: 'Variance — Actual vs Baseline', value: r3(v.perf_vs_target) },
        { metric: 'Variance — Actual vs Rolling', value: r3(v.perf_vs_forecast) },
      ],
    });
    // monthly comparison
    sheets.push({
      name: 'Monthly Comparison',
      title: `Monthly Baseline vs Rolling vs Actual — ${year}`,
      columns: [
        { header: 'Month', key: 'month', width: 12 },
        { header: 'Baseline', key: 'target', width: 16, align: 'right', numFmt: '#,##0.000' },
        { header: 'Rolling', key: 'forecast', width: 16, align: 'right', numFmt: '#,##0.000' },
        { header: 'Actual', key: 'performance', width: 16, align: 'right', numFmt: '#,##0.000' },
      ],
      rows: (analytics.months || []).map((m, i) => ({ month: MONTHS[i], target: r3(m.target), forecast: r3(m.forecast), performance: r3(m.performance) })),
      totalRow: { month: 'TOTAL', target: r3(t.target), forecast: r3(t.forecast), performance: r3(t.performance) },
    });
  }

  // 2) Baseline / Rolling / Actual grids
  sheets.push(gridSheet('Baseline', `Baseline Forecast — ${year}`, gridRows(bVals, pricing, catName, acctName)));
  sheets.push(gridSheet('Rolling', `Rolling Forecast — ${year}`, gridRows(rVals, pricing, catName, acctName)));
  sheets.push(gridSheet('Actual Sales', `Actual Sales Performance — ${year}`, gridRows(aVals, pricing, catName, acctName)));

  // 3) Monthly detail (each month with its products) — baseline + actual
  sheets.push(monthlyDetailSheet('Monthly (Baseline)', `Monthly Detail — Baseline ${year}`, bVals, pricing, catName, acctName));
  sheets.push(monthlyDetailSheet('Monthly (Actual)', `Monthly Detail — Actual ${year}`, aVals, pricing, catName, acctName));

  // 4) Service Orders grid (from confirmed & approved service orders)
  if (soGrid && soGrid.values) {
    const soPricing = soGrid.pricing || pricing;
    sheets.push(gridSheet('Service Orders', `Confirmed & Approved Service Orders — ${year}`, gridRows(soGrid.values, soPricing, catName, acctName)));
    sheets.push(monthlyDetailSheet('Monthly (Service Orders)', `Monthly Detail — Service Orders ${year}`, soGrid.values, soPricing, catName, acctName));
  }

  // 5) Accounts sheet — count, creation dates, month-over-month growth
  if (accounts && accounts.length) {
    const accRows = [...accounts]
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
      .map((a, i) => ({
        n: i + 1, name: a.name, city: a.city || '', country: a.country || '',
        deals: Number(a.deal_count) || 0, contacts: Number(a.contact_count) || 0,
        created: a.created_at ? new Date(a.created_at).toLocaleDateString() : '',
      }));
    sheets.push({
      name: 'Accounts',
      title: `Customer Accounts (${accounts.length})`,
      subtitle: `Generated ${new Date().toLocaleDateString()}`,
      columns: [
        { header: '#', key: 'n', width: 6, align: 'right' },
        { header: 'Account', key: 'name', width: 30 },
        { header: 'City', key: 'city', width: 16 },
        { header: 'Country', key: 'country', width: 16 },
        { header: 'Deals', key: 'deals', width: 10, align: 'right' },
        { header: 'Contacts', key: 'contacts', width: 10, align: 'right' },
        { header: 'Created', key: 'created', width: 14 },
      ],
      rows: accRows,
    });

    // Month-over-month new accounts + cumulative growth
    const byMonth = {};
    accounts.forEach(a => {
      if (!a.created_at) return;
      const d = new Date(a.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    });
    const keys = Object.keys(byMonth).sort();
    let cum = 0;
    const growthRows = keys.map((k, i) => {
      const added = byMonth[k]; const prev = cum; cum += added;
      const growthPct = prev > 0 ? Math.round((added / prev) * 100) : (added > 0 ? 100 : 0);
      return { month: k, added, total: cum, growth: `${growthPct}%` };
    });
    sheets.push({
      name: 'Account Growth',
      title: 'Account Growth — month over month',
      columns: [
        { header: 'Month', key: 'month', width: 14 },
        { header: 'New Accounts', key: 'added', width: 14, align: 'right' },
        { header: 'Cumulative Total', key: 'total', width: 16, align: 'right' },
        { header: 'Growth vs prev. total', key: 'growth', width: 18, align: 'right' },
      ],
      rows: growthRows,
    });
  }

  // 6) Inventory sheet — products with stock, price, cost, value
  if (products && products.length) {
    const invRows = products.map(p => {
      const stock = Number(p.stock_qty) || 0, price = Number(p.unit_price) || 0, cost = Number(p.cost_price) || 0;
      return {
        name: p.name, code: p.code || p.sku || '', category: p.category_name || '',
        stock, reorder: Number(p.reorder_level) || 0,
        price: r3(price), cost: r3(cost),
        stock_value: r3(stock * cost),
        low: (Number(p.reorder_level) || 0) > 0 && stock <= (Number(p.reorder_level) || 0) ? 'LOW' : '',
      };
    });
    sheets.push({
      name: 'Inventory',
      title: `Inventory (${products.length} products)`,
      subtitle: `Generated ${new Date().toLocaleDateString()}`,
      columns: [
        { header: 'Product', key: 'name', width: 28 },
        { header: 'Code', key: 'code', width: 14 },
        { header: 'Category', key: 'category', width: 18 },
        { header: 'Stock', key: 'stock', width: 10, align: 'right' },
        { header: 'Reorder', key: 'reorder', width: 10, align: 'right' },
        { header: 'Unit Price', key: 'price', width: 12, align: 'right', numFmt: '#,##0.000' },
        { header: 'Unit Cost', key: 'cost', width: 12, align: 'right', numFmt: '#,##0.000' },
        { header: 'Stock Value', key: 'stock_value', width: 14, align: 'right', numFmt: '#,##0.000' },
        { header: 'Status', key: 'low', width: 10, align: 'center' },
      ],
      rows: invRows,
      totalRow: { name: 'TOTAL', stock_value: r3(invRows.reduce((s, r) => s + (Number(r.stock_value) || 0), 0)) },
    });
  }

  await downloadStyledWorkbook(`Sales_Forecast_${year}_${new Date().toISOString().slice(0, 10)}.xlsx`, sheets);
}
